import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, Undo2, Coins, FileDown, FileSpreadsheet, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const zl = (n) => `${(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const dt = (iso) => { try { return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return (iso || "").slice(0, 10); } };
const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const quarterRange = (y, q) => { const sm = (q - 1) * 3; return [fmtDate(new Date(y, sm, 1)), fmtDate(new Date(y, sm + 3, 0))]; };

export const Settlements = ({ mine = false }) => {
  const now = new Date();
  const curQ = Math.floor(now.getMonth() / 3) + 1;
  const curY = now.getFullYear();
  const [initFrom, initTo] = quarterRange(curY, curQ);
  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);
  const [qYear, setQYear] = useState(curY);
  const [quarter, setQuarter] = useState(curQ);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [editDate, setEditDate] = useState(null);
  const [dateVal, setDateVal] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(mine ? "/my-commissions" : "/settlements", { params: { from, to } }); setRows(data); setSelected(new Set()); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [from, to]);

  const settledFlag = tab === "settled";
  const visible = rows.filter((r) => r.settled === settledFlag);
  const groups = {};
  visible.forEach((r) => { (groups[r.employee_id] ||= { name: r.employee_name, orders: [] }).orders.push(r); });
  const grand = visible.reduce((a, r) => a + r.commission, 0);
  const L = mine
    ? { pending: "Do wypłaty", settled: "Wypłacone" }
    : { pending: "Do rozliczenia", settled: "Rozliczone" };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExp = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const startEditDate = (o) => { setEditDate(o.id); setDateVal((o.closed_at || "").slice(0, 10)); };
  const saveDate = async (o) => {
    if (!dateVal || dateVal === (o.closed_at || "").slice(0, 10)) { setEditDate(null); return; }
    setSavingDate(true);
    try {
      await api.patch(`/orders/${o.id}/closed-date`, { date: dateVal });
      toast.success(`Zmieniono datę zamknięcia ${o.number} → ${dt(dateVal)}`);
      setEditDate(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingDate(false); }
  };

  const confirm = useConfirm();
  const mark = async (ids, settled) => {
    if (!ids.length) return;
    if (!(await confirm({ title: settled ? "Rozliczyć zaznaczone?" : "Cofnąć rozliczenie?", description: `${settled ? "Oznaczyć jako rozliczone" : "Cofnąć rozliczenie dla"} ${ids.length} zamówień?`, confirmText: settled ? "Rozlicz" : "Cofnij" }))) return;
    try {
      const { data } = await api.post("/settlements/mark", { order_ids: ids, settled });
      toast.success(`${settled ? "Rozliczono" : "Cofnięto rozliczenie"}: ${data.count} zam. (${zl(data.total)})`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="settlements-view">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-amber-500/30 bg-amber-400/[0.04] px-4 py-3" data-testid="quarter-export-panel">
        <div>
          <Label className="text-xs">Rozliczenie kwartalne — rok</Label>
          <Select value={String(qYear)} onValueChange={(v) => { const y = Number(v); setQYear(y); const [f, t] = quarterRange(y, quarter); setFrom(f); setTo(t); }}>
            <SelectTrigger data-testid="quarter-year" className="mt-1 h-9 w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Kwartał</Label>
          <Select value={String(quarter)} onValueChange={(v) => { const q = Number(v); setQuarter(q); const [f, t] = quarterRange(qYear, q); setFrom(f); setTo(t); }}>
            <SelectTrigger data-testid="quarter-select" className="mt-1 h-9 w-[210px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1 (styczeń–marzec)</SelectItem>
              <SelectItem value="2">Q2 (kwiecień–czerwiec)</SelectItem>
              <SelectItem value="3">Q3 (lipiec–wrzesień)</SelectItem>
              <SelectItem value="4">Q4 (październik–grudzień)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!mine && <Button data-testid="quarter-export-pdf" onClick={() => downloadBlob(`/settlements/export/pdf?year=${qYear}&quarter=${quarter}`, `rozliczenie-Q${quarter}-${qYear}.pdf`, "PDF")} className="h-9 gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><FileDown className="h-4 w-4" />Eksport PDF</Button>}
        {!mine && <Button data-testid="quarter-export-csv" variant="outline" onClick={() => downloadBlob(`/settlements/export/csv?year=${qYear}&quarter=${quarter}`, `rozliczenie-Q${quarter}-${qYear}.csv`, "CSV")} className="h-9 gap-2 font-semibold"><FileSpreadsheet className="h-4 w-4" />Eksport CSV</Button>}
        {mine && <Button data-testid="my-export-pdf" onClick={() => downloadBlob(`/my-commissions/export/pdf?year=${qYear}&quarter=${quarter}`, `moje-prowizje-Q${quarter}-${qYear}.pdf`, "PDF")} className="h-9 gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><FileDown className="h-4 w-4" />Eksport PDF</Button>}
        {mine && <Button data-testid="my-export-csv" variant="outline" onClick={() => downloadBlob(`/my-commissions/export/csv?year=${qYear}&quarter=${quarter}`, `moje-prowizje-Q${quarter}-${qYear}.csv`, "CSV")} className="h-9 gap-2 font-semibold"><FileSpreadsheet className="h-4 w-4" />Eksport CSV</Button>}
        <span className="ml-auto self-center text-xs text-muted-foreground max-w-[280px]">Wybór kwartału filtruje listę {mine ? "poniżej" : "poniżej i eksport"} (wg daty zamknięcia zamówień).</span>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card px-4 py-3">
        <div><Label className="text-xs">Data zamknięcia od</Label><Input data-testid="settle-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-[170px]" /></div>
        <div><Label className="text-xs">do</Label><Input data-testid="settle-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-[170px]" /></div>
        <Button data-testid="settle-apply" onClick={load} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Pokaż</Button>
        <div className="ml-auto flex items-center gap-2 rounded-lg bg-secondary px-4 py-2">
          <Coins className="h-4 w-4 text-amber-400" />
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{settledFlag ? L.settled : L.pending}:</span>
          <span className="font-display text-lg font-extrabold tabular-nums" data-testid="settle-grand-total">{zl(grand)}</span>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected(new Set()); }}>
        <TabsList data-testid="settle-tabs">
          <TabsTrigger value="pending" data-testid="settle-tab-pending">{L.pending}</TabsTrigger>
          <TabsTrigger value="settled" data-testid="settle-tab-settled">{L.settled}</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {!mine && selected.size > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-400/10 px-4 py-2.5" data-testid="settle-action-bar">
              <span className="text-sm font-semibold">Zaznaczono: {selected.size} zam.</span>
              <Button data-testid="settle-mark-selected" size="sm" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"
                onClick={() => mark([...selected], !settledFlag)}>
                {settledFlag ? <><Undo2 className="h-4 w-4" />Cofnij rozliczenie</> : <><CheckCircle2 className="h-4 w-4" />Rozlicz zaznaczone</>}
              </Button>
            </div>
          )}

          {loading ? <div className="p-8 text-center text-muted-foreground">Ładowanie…</div>
            : Object.keys(groups).length === 0 ? <div data-testid="settle-empty" className="rounded-xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">Brak {mine ? "prowizji" : "zamówień"} {settledFlag ? (mine ? "wypłaconych" : "rozliczonych") : (mine ? "do wypłaty" : "do rozliczenia")} w tym zakresie.</div>
            : Object.entries(groups).map(([empId, g]) => {
              const sum = g.orders.reduce((a, o) => a + o.commission, 0);
              const isOpen = expanded.has(empId);
              const ids = g.orders.map((o) => o.id);
              return (
                <div key={empId} data-testid={`settle-emp-${empId}`} className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-secondary/50">
                    <button data-testid={`settle-emp-toggle-${empId}`} onClick={() => toggleExp(empId)} className="flex items-center gap-2 flex-1 text-left">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <span className="font-display text-lg font-extrabold">{g.name}</span>
                      <span className="text-xs font-mono text-muted-foreground">({g.orders.length} zam.)</span>
                    </button>
                    <span className="font-display text-xl font-extrabold tabular-nums text-amber-300">{zl(sum)}</span>
                    {!mine && <Button data-testid={`settle-emp-all-${empId}`} size="sm" variant="outline" className="gap-2 font-semibold"
                      onClick={() => mark(ids, !settledFlag)}>
                      {settledFlag ? <><Undo2 className="h-4 w-4" />Cofnij wszystkie</> : <><CheckCircle2 className="h-4 w-4" />Rozlicz wszystkie</>}
                    </Button>}
                  </div>
                  {isOpen && (
                    <table className="w-full text-sm">
                      <thead className="text-muted-foreground font-mono text-[11px] uppercase tracking-wider border-t border-border">
                        <tr>{!mine && <th className="w-10 px-4 py-2" />}<th className="text-left px-3 py-2">Nr</th><th className="text-left px-3 py-2">Klient</th><th className="text-left px-3 py-2">Zamknięto</th><th className="text-right px-4 py-2">Prowizja</th></tr>
                      </thead>
                      <tbody>
                        {g.orders.map((o) => (
                          <tr key={o.id} data-testid={`settle-order-${o.id}`} className="border-t border-border hover:bg-accent/40">
                            {!mine && <td className="px-4 py-2.5"><Checkbox data-testid={`settle-check-${o.id}`} className="h-5 w-5" checked={selected.has(o.id)} onCheckedChange={() => toggleSel(o.id)} /></td>}
                            <td className="px-3 py-2.5 font-mono text-xs font-bold text-amber-300">{o.number}</td>
                            <td className="px-3 py-2.5 font-medium">{o.customer_name}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">
                              {mine ? dt(o.closed_at) : editDate === o.id ? (
                                <div className="flex items-center gap-1">
                                  <Input data-testid={`settle-date-input-${o.id}`} type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="h-8 w-[150px] text-xs" />
                                  <Button data-testid={`settle-date-save-${o.id}`} size="icon" variant="ghost" className="h-7 w-7 text-emerald-400" disabled={savingDate} onClick={() => saveDate(o)}><Check className="h-4 w-4" /></Button>
                                  <Button data-testid={`settle-date-cancel-${o.id}`} size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditDate(null)}><X className="h-4 w-4" /></Button>
                                </div>
                              ) : (
                                <button data-testid={`settle-date-edit-${o.id}`} onClick={() => startEditDate(o)} className="group inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-accent transition-colors" title="Zmień datę zamknięcia">
                                  {dt(o.closed_at)}
                                  <Pencil className="h-3 w-3 text-amber-400/70 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold tabular-nums">{zl(o.commission)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
        </TabsContent>
      </Tabs>
    </div>
  );
};
